<template>
	<component :is="wrapper" :title="title">
		<!--Obeserver to wrap providers-->
		<ValidationObserver
			ref="flow-form"
			:tag="'form'"
			class="flow-form"
			@submit.prevent="validateAndSubmit()"
		>
			<!--native element to wrap all field with specific direction-->
			<div
				class="flow-form-builder"
				:data-direction="direction"
				:in-accordion="accordion"
				:data-old-safari="isOldSafari()"
			>
				<!--Provider to have validation related config -->
				<ValidationProvider
					v-for="(field, idx) in computedFields"
					v-slot="{ errors, validate, passed }"
					:key="idx"
					:name="getFieldLabel(field)"
					:rules="'rules' in field ? field.rules : undefined"
					:custom-messages="'customMessages' in field ? field.customMessages : undefined"
					:vid="field.id"
					:mode="mode"
					slim
				>
					<!-- :immediate="field.immediate ? field.immediate : false" -->
					<FormField
						:inline="'inline' in field ? field.inline : false"
						:class="['flow-form-builder-' + field.type, field.cssClass]"
					>
						<!-- :style="field.style" -->

						<!--Display Field Label-->
						<template v-if="field.type !== 'accordion' && (field.label || field.info)" #label>
							<!--Label container-->
							<Container :padding="0" overflow="visible">
								<!--Label-->
								<Typography
									v-if="field.label"
									:type="field.label.type"
									:color="field.label.color"
									:weight="field.label.weight"
									:transform="field.label.transform"
									:overflow="field.label.overflow"
									:link="field.label.link"
									:data-qa-label-for="field.qaId || field.id"
									@click.native="handleTextClick(field.label)"
								>
									{{ field.label.text }}
								</Typography>

								<slot :name="field.name + '_label_actions'" />
								<!--Info with icon-->
								<!-- prettier-ignore -->
								<InfoPopover
									v-if="
										field.info &&
										typeof field.info.icon === 'object' &&
										!$slots[field.name + '_info_tootip']
									"
									container="div.flow-form-builder"
								>
									<!--Info icon-->
									<Icon
										:data-qa-info-icon-for="field.qaId || field.id"
										:name="field.info.icon.name"
										:size="field.info.icon.size"
										:type="field.info.icon.type"
										:state="field.info.icon.state"
									/>
									<template #body>
										<Container direction="column" :padding="0" :gap="8">
											<Typography
												v-if="field.info"
												:type="field.info.type"
												:color="'white'"
												:weight="field.info.weight"
												:transform="field.info.transform"
												:data-qa-info-for="field.qaId || field.id"
												:overflow="field.info.overflow"
											>
												{{ field.info.text }}
											</Typography>
										</Container>
									</template>
								</InfoPopover>
								<InfoPopover
									v-if="$slots[field.name + '_info_tootip']"
									:max-width="'50vw'"
									container="div.flow-form-builder"
								>
									<!--Info icon-->
									<Icon
										:name="'i-question-filled'"
										:size="'small'"
										:data-qa-info-icon-for="field.qaId || field.id"
										:type="'filled'"
										:state="'font-light'"
									/>
									<template #body>
										<slot
											v-if="$slots[field.name + '_info_tootip']"
											:name="field.name + '_info_tootip'"
										/>
									</template>
								</InfoPopover>
							</Container>
						</template>

						<!--Info without icon (i.e. when icon set to false)-->
						<template v-if="enableInfoSlot(field)" #info>
							<Typography
								v-if="field.info && typeof field.info.icon === 'boolean' && !field.info.icon"
								:type="field.info.type"
								:color="field.info.color"
								:weight="field.info.weight"
								:transform="field.info.transform"
								:overflow="field.info.overflow"
								:link="field.info.link"
								:data-qa-info-for="field.qaId || field.id"
								@click.native="handleTextClick(field.info)"
							>
								{{ field.info.text }}
							</Typography>
							<Container
								v-if="'flags' in field && field.flags && field.flags.length > 0"
								:gap="12"
								:padding="0"
								overflow="visible"
							>
								<template v-for="flag in field.flags">
									<Checkbox
										:key="'check-' + flag.name"
										:checked.sync="flag.value"
										@input="emitInput()"
									/>
									<Typography :key="'label-' + flag.name" type="p2">
										{{ flag.label }}
									</Typography>
								</template>
							</Container>
						</template>

						<!--Help displayed at bottom of field-->
						<template v-if="field.help || field.type === 'file' || errors" #help>
							<Container v-if="field.type === 'file'" direction="column" :padding="0" :gap="8">
								<Slab
									v-for="(file, fdx) in values[field.id]"
									:key="fdx + '_' + file.name"
									:size="field.multiple ? 'small' : 'large'"
									class="form-selected-file"
								>
									<template #primary-action>
										<Icon :effects="false" size="small" type="filled" name="i-text" />
									</template>
									<Typography type="p2">
										{{ file.name }}
									</Typography>
									<template #secondary-actions>
										<Icon
											name="i-close"
											size="x-small"
											type="filled"
											@click="removeFile(file.name, field, validate)"
										/>
									</template>
								</Slab>
							</Container>
							<slot :name="field.name + '_help'" :help="field.help">
								<Typography
									v-if="field.help"
									:type="field.help.type"
									:color="field.help.color"
									:weight="field.help.weight"
									:transform="field.help.transform"
									:overflow="field.help.overflow"
									:link="field.help.link"
									:data-qa-help-for="field.qaId || field.id"
									@click.native="handleTextClick(field.help)"
								>
									{{ field.help.text }}
								</Typography>
							</slot>
							<template v-if="errors && errors.length > 0">
								<Typography
									v-for="(error, eidx) in errors"
									:key="eidx"
									:data-qa-error-for="field.qaId || field.id"
									:type="'p2'"
									color="error"
								>
									{{ error }}
								</Typography>
							</template>
						</template>

						<!--Form field displayed dynamically-->
						<template v-if="noComponentRequired.indexOf(field.type) === -1">
							<!--Dynamic component : This is where actual Vue component rendered-->
							<component
								:is="field.component"
								v-if="checkVisibility(field)"
								:ref="field.id"
								:data-qa-id="field.qaId || field.id"
								:[field.errorProp]="checkErrorsState(field, errors, passed)"
								v-bind="field.props"
								:[field.model]="values[field.id]"
								@input="handleInput($event, field, validate)"
								@submit="validateAndSubmit()"
								@click="handleClick($event, field)"
								@initialValues="handleInitialValues($event, field)"
							>
								<template v-if="valuesInSlot.indexOf(field.type) > -1 && 'value' in field">
									{{ field.value }}
								</template>

								<!--START : Multiline selection-->

								<template v-if="field.type === 'select'" #tag="props">
									<slot
										v-if="$scopedSlots[field.name + '_tag']"
										:name="field.name + '_tag'"
										:option="props.option"
										:search="props.search"
										:remove="props.removeElement"
									/>
								</template>
								<template v-if="field.type === 'select'" #singleLabel="props">
									<slot
										v-if="$scopedSlots[field.name + '_selected_option']"
										:name="field.name + '_selected_option'"
										:option="props.option"
									/>
									<template v-if="!$scopedSlots[field.name + '_selected_option']">
										<Typography
											v-if="typeof props.option === 'string'"
											v-tooltip="props.option"
											overflow="ellipsis"
											type="p2"
											data-qa-option
											>{{ props.option }}</Typography
										>
										<Container
											v-else-if="typeof props.option === 'object' && props.option.header"
											align="space-between center"
											:gap="4"
											:grow="1"
											padding="8px 0px 8px 0px"
										>
											<Container direction="column" :grow="1" :gap="4" :padding="0">
												<Typography type="p1" data-qa-option>{{ props.option.header }}</Typography>
												<Typography type="p2" color="light">{{ props.option.details }}</Typography>
											</Container>
											<Typography type="p2" color="light">{{ props.option.meta }}</Typography>
										</Container>
										<Container
											v-else-if="typeof props.option === 'object' && props.option.icon"
											:gap="8"
											:grow="1"
											padding="8px 24px 8px 0px"
										>
											<Icon :name="props.option.icon" type="filled" size="small" :effects="false" />
											<Typography type="p2" data-qa-option>{{ props.option.label }}</Typography>
										</Container>
									</template>
								</template>
								<template v-if="field.type === 'select'" #option="props">
									<slot
										v-if="$scopedSlots[field.name + '_option']"
										:name="field.name + '_option'"
										:option="props.option"
										:search="props.search"
										:index="props.index"
									/>
									<template v-if="!$scopedSlots[field.name + '_option']">
										<Typography
											v-if="typeof props.option === 'string'"
											v-tooltip="props.option"
											type="p2"
											overflow="ellipsis"
											data-qa-option
											>{{ props.option }}</Typography
										>
										<Container
											v-else-if="typeof props.option === 'object' && props.option.header"
											align="space-between center"
											:gap="4"
											:grow="1"
											padding="8px 24px 8px 0px"
										>
											<Container direction="column" :grow="1" :gap="4" :padding="0">
												<Typography type="p1" data-qa-option>{{ props.option.header }}</Typography>
												<Typography type="p2" color="light">{{ props.option.details }}</Typography>
											</Container>
											<Typography type="p2" color="light">{{ props.option.meta }}</Typography>
										</Container>
										<Container
											v-else-if="typeof props.option === 'object' && props.option.icon"
											:gap="8"
											:grow="1"
											padding="8px 24px 8px 0px"
										>
											<Icon :name="props.option.icon" type="filled" size="small" :effects="false" />
											<Typography type="p2" data-qa-option>{{ props.option.label }}</Typography>
										</Container>
									</template>
								</template>

								<template
									v-if="field.type === 'select' && field.props.addNewOptionOnNoResult"
									#noResult="{ search }"
								>
									<Container :padding="0" :gap="8" @click.native="addOption(field, search)">
										<Typography type="p1" color="dark">{{ search }}</Typography>
										<Typography v-if="field.type === 'select'" type="p2" color="light">{{
											field.props.noResultMessage
										}}</Typography>
									</Container>
								</template>
								<!--END : Multiline selection-->
								<template
									v-if="field.type === 'radio' || field.type === 'checkbox'"
									#option_info="props"
								>
									<slot
										v-if="$scopedSlots[field.name + '_option_info']"
										:name="field.name + '_option_info'"
										:option="props.option"
									></slot>
								</template>
							</component>
						</template>
					</FormField>
				</ValidationProvider>
			</div>
		</ValidationObserver>
	</component>
</template>
<script lang="ts">
import InputText from "./../FormElements/InputText.vue";

import { cloneDeep } from "lodash-es";
import { extend, ValidationObserver, ValidationProvider } from "vee-validate";
import * as rules from "vee-validate/dist/rules";
import FormField from "./..//FormElements/FormField.vue";
import AvatarUpload from "./../AvatarUpload/AvatarUpload.vue";
import Button from "./../Button/Button.vue";
import Container from "./../Container/Container.vue";
import Divider from "./../Divider/Divider.vue";
import FileUpload from "./../FileUpload/FileUpload.vue";
import Checkbox from "./../FormElements/Checkbox.vue";
import EmojiPicker from "./../FormElements/EmojiPicker.vue";
import RadioButton from "./../FormElements/RadioButton.vue";
import SwitchBox from "./../FormElements/SwitchBox/SwitchBox.vue";
import Icon from "./../Icon/Icon.vue";
import InfoPopover from "./../InfoPopover/InfoPopover.vue";
import Multiselect from "./../Multiselect/Multiselect.vue";
import Multisuggest from "../Multiselect/Multisuggest.vue";
import SearchInput from "./../SearchInput/SearchInput.vue";
import Slab from "./../Slab/Slab.vue";
import Typography from "./../Typography/Typography.vue";
import AccordionField from "./AccordionField.vue";
import ButtonIcon from "./ButtonIcon.vue";
import CheckOptions from "./CheckOptions.vue";
import {
	FormBuilderComputedField,
	FormBuilderDirection,
	FormBuilderField,
	FormBuilderFields,
	FormBuilderIDField,
	FormBuilderInfoText,
	FormBuilderInputTextField,
	FormBuilderInputTextNumberField,
	FormBuilderMode,
	FormBuilderText
} from "./formbuilder-types";

// eslint-disable-next-line import/no-unresolved
import { ValidationRule } from "vee-validate/dist/types/types";
import { defineComponent, PropType } from "vue";
import BrowserVersion from "../utils/BrowserVersion";
import DateTimePicker from "./../FormElements/DateTimePicker/DateTimePicker.vue";
import { VTooltip } from "v-tooltip";

export default defineComponent({
	name: "FormBuilder",

	components: {
		InputText,
		Multiselect,
		Multisuggest,
		SearchInput,
		Button,
		Checkbox,
		RadioButton,
		FileUpload,
		SwitchBox,
		FormField,
		Typography,
		Container,
		Icon,
		InfoPopover,
		EmojiPicker,
		CheckOptions,
		Slab,
		Divider,
		ValidationProvider,
		ValidationObserver,
		ButtonIcon,
		AvatarUpload,
		AccordionField,
		DateTimePicker
	},
	directives: {
		tooltip: VTooltip
	},
	props: {
		fields: {
			type: Array as PropType<FormBuilderField[]>,
			required: true,
			default: () => []
		},

		// eslint-disable-next-line @typescript-eslint/no-empty-function
		customRules: {
			type: Object as PropType<Record<string, any>>,
			default: () => ({})
		},

		// eslint-disable-next-line @typescript-eslint/no-empty-function
		externalErrors: {
			type: Object as PropType<Record<string, any>>,
			default: () => ({})
		},

		resetValuesOnFieldUpdate: {
			type: Boolean,
			default: () => false
		},

		accordion: {
			type: Boolean,
			default: () => false
		},

		direction: {
			type: String as PropType<FormBuilderDirection>,
			default: () => "column"

			// validator(value: string): boolean {
			// 	return ["row", "column"].includes(value);
			// }
		},

		title: {
			type: String,
			default: () => null
		},

		mode: {
			type: String as PropType<FormBuilderMode>,
			default: () => "aggressive"

			// validator(value: string): boolean {
			// 	return ["aggressive", "passive"].includes(value);
			// }
		}
	},

	emits: ["initialValues", "beforeInput", "input", "submit"],

	data: () => ({
		/**
		 * Values are passed or displayed in default slot of component
		 */
		valuesInSlot: ["button", "submit"],

		/**
		 * Field property to propogate as component prop
		 */
		propsToPropogate: ["placeholder", "options", "name", "title", "subTitle", "accept"],

		/**
		 * No component required to display this type
		 */
		noComponentRequired: ["header"],

		/**
		 * No need to return values
		 */
		nonInputTypes: ["button", "submit", "separator", "header", "icon", "button-icon"],

		/**
		 * Possible types of fields and their default props and other config
		 */
		types: {
			text: {
				component: "InputText",
				props: {},
				model: "value"
			},
			textarea: {
				component: "InputText",
				props: {
					multiline: true
				},
				model: "value"
			},
			select: {
				component: "Multiselect",
				props: {
					showLabels: false,
					headerInOption: true,
					multiple: false,
					allowEmpty: false,
					noResultMessage: "(Add New Option)",
					addNewOptionOnNoResult: false,
					openDirection: "bottom",
					maxHeight: 200,
					customLabel(option: any): string {
						if (typeof option === "object" && option.header) {
							return `${option.header} – ${option.details} - ${option.meta}`;
						} else if (typeof option === "object" && option.icon) {
							return `${option.icon} – ${option.label}`;
						} else {
							return option;
						}
					},
					options: []
				},
				model: "value"
			},
			suggest: {
				component: "Multisuggest",
				props: {
					options: []
				},
				model: "value"
			},
			search: {
				component: "SearchInput",
				model: "value",
				props: {}
			},
			avatar: {
				component: "AvatarUpload",
				model: "file",
				props: {}
			},
			button: {
				component: "Button",
				model: "value",
				props: { type: "default" }
			},
			submit: {
				component: "Button",
				model: "value",
				props: { type: "success" }
			},
			password: {
				component: "InputText",
				props: {
					type: "password"
				},
				model: "value"
			},
			number: {
				component: "InputText",
				props: {
					type: "number"
				},
				model: "value"
			},
			email: {
				component: "InputText",
				props: {
					type: "email"
				},
				model: "value"
			},
			checkbox: {
				component: "CheckOptions",
				props: {},
				model: "value"
			},
			radio: {
				component: "CheckOptions",
				model: "value",
				props: { type: "radio" }
			},
			file: {
				component: "FileUpload",
				model: "value",
				multiple: true,
				props: { title: "Drag and drop files" }
			},
			switch: {
				component: "SwitchBox",
				model: "on",
				props: {}
			},
			emoji: {
				component: "EmojiPicker",
				model: "selected",
				props: {}
			},
			separator: {
				component: "Divider",
				model: "data-form-field",
				props: {
					state: "default",
					size: 1,
					type: "solid"
				}
			},
			header: {
				component: "Typography",
				props: {
					type: "h3",
					weight: "bold",
					color: "dark"
				}
			},
			icon: {
				component: "Icon",
				model: "data-form-field",
				props: {
					type: "filled"
				}
			},
			"button-icon": {
				component: "ButtonIcon",
				model: "value",
				props: {}
			},
			date: {
				component: "DateTimePicker",
				model: "value",
				props: { mode: "date" }
			},
			hidden: {
				component: "input",
				model: "value",
				props: { type: "hidden" }
			}
		} as Record<string, any>,

		/**
		 * Default field label style config
		 */
		label: {
			type: "p2",
			color: "dark",
			weight: "regular",
			transform: "none",
			overflow: "wrap",
			link: false
		} as FormBuilderText,

		/**
		 * Default help style config
		 */
		help: {
			type: "p2",
			color: "light",
			weight: "regular",
			transform: "none",
			overflow: "wrap",
			link: false
		} as FormBuilderText,

		/**
		 * Default info style config
		 */
		info: {
			type: "p2",
			color: "default",
			weight: "regular",
			transform: "none",
			overflow: "wrap",
			link: false,
			/**
			 * Default icon on which info displayed
			 */
			icon: {
				name: "i-question-filled",
				size: "small",
				type: "filled",
				state: "font-light"
			}
		} as FormBuilderInfoText,

		/**
		 * All Field values
		 */
		values: {} as { [key: string]: any | any[] }
	}),

	computed: {
		wrapper() {
			if (this.accordion) {
				return "AccordionField";
			}
			return "div";
		},

		/**
		 * Converts simplyfied fields to respective Vue component
		 */
		computedFields(): FormBuilderComputedField[] {
			const _fields: FormBuilderComputedField[] = [];

			/**
			 * Index used to assign id to child forms if not specified
			 */
			let childFormIndex = 0;
			this.fields.forEach(field => {
				/**
				 * Check if child form is defined and display in row direction
				 */
				if (Array.isArray(field)) {
					// eslint-disable-next-line no-console
					console.warn("FormBuilder: Nested array form is deprecated, use 'array' type instead");

					childFormIndex += 1;

					/**
					 * Finds if id specified for child form
					 */
					const idField = field.find(isIDField);

					const id = idField ? idField.value : `Child_Form_${childFormIndex}`;

					const computedField: FormBuilderComputedField = {
						inline: false,
						type: "array",
						component: "FormBuilder",
						model: "value",
						errorProp: "error-state",
						value: [],
						props: {
							fields: field,
							direction: "row",
							customRules: this.customRules,
							externalErrors: this.externalErrors
						},
						id,
						name: id
					};

					if (!this.isFieldInValues(computedField)) {
						this.values[id] = null;
					}

					_fields.push(computedField);
				} else if (field.type === "array") {
					const id = this.getFieldId(field);

					const computedField: FormBuilderComputedField = {
						inline: false,
						type: "array",
						component: "FormBuilder",
						model: "value",
						errorProp: "error-state",
						value: [],
						props: {
							fields: field.value,
							direction: field.direction || "row",
							customRules: this.customRules,
							externalErrors: this.externalErrors
							// class: field.cssClass
						},
						name: field.name,
						id
					};

					if (!this.isFieldInValues(computedField)) {
						this.values[id] = null;
					}

					_fields.push(computedField);
				} else if (field.type === "accordion") {
					const id = this.getFieldId(field);

					const computedField: FormBuilderComputedField = {
						inline: false,
						type: "accordion",
						component: "FormBuilder",
						model: "value",
						errorProp: "error-state",
						value: field.value,
						props: {
							accordion: true,
							fields: field.value,
							title: field.title,
							direction: "column",
							customRules: this.customRules,
							externalErrors: this.externalErrors
						},
						name: field.name,
						id
					};

					if (!this.isFieldInValues(computedField)) {
						this.values[id] = null;
					}

					_fields.push(computedField);
				} else if (field.type !== "id") {
					const computedField: FormBuilderComputedField = {
						inline: false,
						rawField: field,
						...cloneDeep(this.types[field.type]),
						...field
					};

					// This is just to make TS happy
					if (computedField.type === "id") {
						return;
					}

					/**
					 * Extending props if any
					 */
					if ("props" in computedField && computedField.props) {
						computedField.props = {
							...cloneDeep(this.types[computedField.type]).props,
							...computedField.props
						};
					}

					/**
					 * Checks label type and covert it to appropriate object
					 */
					if (typeof computedField.label === "string") {
						if (computedField.type === "header") {
							computedField.label = {
								...this.types[computedField.type].props,
								text: computedField.label
							};
						} else {
							computedField.label = { ...this.label, text: computedField.label };
						}
					} else if (typeof computedField.label === "object") {
						computedField.label = { ...this.label, ...computedField.label };
					}

					/**
					 * Checks help type and covert it to appropriate object
					 */
					if (typeof computedField.help === "string") {
						computedField.help = { ...this.help, text: computedField.help };
					} else if (typeof computedField.help === "object") {
						computedField.help = { ...this.help, ...computedField.help };
					}

					/**
					 * Checks info type and covert it to appropriate object
					 */
					if (typeof computedField.info === "string") {
						computedField.info = { ...this.info, text: computedField.info };
					} else if (typeof computedField.info === "object") {
						computedField.info = { ...this.info, ...computedField.info };
					}

					/**
					 * propogate direct property to Vue props
					 */
					this.propsToPropogate.forEach(property => {
						//@ts-expect-error
						if (computedField[property]) {
							//@ts-expect-error
							computedField.props[property] = computedField[property];
						}
					});

					/**
					 * Icon field specific convertion
					 */
					if (computedField.type === "icon") {
						if (typeof computedField.value === "string") {
							computedField.props.name = computedField.value;
						} else if (typeof computedField.value === "object") {
							Object.keys(computedField.value).forEach(prop => {
								//@ts-expect-error
								computedField.props[prop] = computedField.value[prop];
							});
						}
					}

					/**
					 * Assign id to field , if not specified then label.text is used
					 */
					computedField.id = this.getFieldId(field);

					/**
					 * Maintaining values object
					 */
					if (
						!this.isFieldInValues(computedField) &&
						"value" in computedField &&
						[...this.noComponentRequired, "separator"].indexOf(computedField.type) === -1
					) {
						/**
						 * Assign default value
						 */
						this.values[computedField.id] =
							computedField.value === undefined ? null : computedField.value;
					}

					/**
					 * Set error state prop
					 */
					computedField.errorProp =
						computedField.component === "Button" ||
						computedField.component === "ButtonIcon" ||
						computedField.component === "Icon"
							? "error-state"
							: "state";

					/**
					 * check if default value is suggested
					 */
					this.checkIfSuggested(computedField, this.values[computedField.id]);
					_fields.push(computedField);
				}
			});
			return _fields;
		}
	},

	watch: {
		externalErrors: {
			deep: true,
			handler() {
				this.setExternalErrors();
			}
		},

		fields() {
			/**
			 * If we want ot reset values on fields update
			 */
			if (this.resetValuesOnFieldUpdate) {
				this.values = {};
			} else {
				/**
				 * Check deleted fields and removes their respective values
				 */
				const ids: string[] = [];
				this.computedFields.forEach(field => {
					ids.push(field.id);
				});

				const valuekeys = Object.keys(this.values);
				let total = valuekeys.length;

				while (total--) {
					if (ids.indexOf(valuekeys[total]) === -1) {
						delete this.values[valuekeys[total]];
					}
				}
			}
		}
	},

	mounted() {
		/**
		 * Register default rules
		 */
		const defaultRules: { [key: string]: ValidationRule } = rules;
		Object.entries(defaultRules).forEach(([key, value]) => {
			extend(key, value);
		});
		/**
		 * Register customRules validation rules if specified
		 */
		if (this.customRules) {
			Object.keys(this.customRules).forEach(rule => {
				extend(rule, this.customRules?.[rule]);
			});
		}

		this.setExternalErrors();

		this.$emit("initialValues", this.getFormValues());
	},

	methods: {
		setExternalErrors() {
			if (this.externalErrors) {
				// We need to reset the validation observer before setting any errors
				// Otherwise repeat errors seem to be lost until the value is changed
				(this.$refs["flow-form"] as any).reset();
				(this.$refs["flow-form"] as any).setErrors(this.externalErrors);
			}
		},

		/**
		 * Checks if field has suggested value and add respective behaviour.
		 * Supported only on InputText for now
		 */
		checkIfSuggested(field: FormBuilderComputedField, value: string) {
			if (field.component === "InputText") {
				const inputField = field as FormBuilderInputTextField | FormBuilderInputTextNumberField;

				if (!inputField.props) {
					inputField.props = {};
				}

				if ("suggested" in inputField && inputField.suggested && inputField.suggested === value) {
					inputField.props.suffixTag = "suggested";
				} else {
					inputField.props.suffixTag = undefined;
				}
			}
		},

		/**
		 * Checks if field has an existing value
		 */
		isFieldInValues(computedField: FormBuilderComputedField) {
			const id = computedField.id;
			const field = this.values[id];
			return field ? true : false;
		},

		/**
		 * Get field id
		 */
		getFieldId(field: FormBuilderField) {
			let id = field.id;
			if (!id) {
				id = this.generateId();
				field.id = id;
			}
			return id;
		},

		generateId(length: number = 5) {
			let result = "";
			const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
			const charactersLength = characters.length;
			for (let i = 0; i < length; i++) {
				result += characters.charAt(Math.floor(Math.random() * charactersLength));
			}
			return result;
		},

		getFormValues() {
			/**
			 * Generates Field Array with id and value
			 */
			const valuesToReturn: Record<string, any> = {};

			Object.keys(this.values).forEach(id => {
				const computedField = this.computedFields.find(cf => cf.id === id);

				if (!computedField) {
					return;
				}

				const flags: Record<string, boolean> = {};

				if ("flags" in computedField && computedField.flags && computedField.flags.length > 0) {
					computedField.flags.forEach(flag => {
						flags[flag.name] = flag.value;
					});
				}

				let name = id;

				if ("name" in computedField && computedField.name) {
					name = computedField.name;
				}

				let currentValue = this.values[id];

				if (computedField.type === "array") {
					currentValue = [currentValue];
				} else if (computedField.type === "number") {
					currentValue = Number(currentValue);
				}

				if (this.nonInputTypes.indexOf(computedField.type) < 0) {
					/**
					 * value for name is already exist then create array
					 */
					if (valuesToReturn[name]) {
						const values = [];
						/**
						 * If existed value is array
						 */
						if (Array.isArray(valuesToReturn[name])) {
							values.push(...valuesToReturn[name]);
						} else {
							values.push(valuesToReturn[name]);
						}
						/**
						 * if value is array
						 */
						if (Array.isArray(currentValue)) {
							values.push(...currentValue);
						} else {
							let val = currentValue;

							if (Object.keys(flags).length > 0) {
								val = { value: currentValue, flags };
							}
							values.push(val);
						}
						valuesToReturn[name] = values;
					} else {
						valuesToReturn[name] = currentValue;
						if (Object.keys(flags).length > 0) {
							valuesToReturn[name] = { value: currentValue, flags };
						}
					}
				}
			});

			return valuesToReturn;
		},

		checkVisibility(field: FormBuilderComputedField) {
			const fieldValue = this.values[field.id];

			if (field.type === "file" && fieldValue) {
				if (fieldValue.length === 0) {
					return true;
				}

				return !(fieldValue.length === 1 && !field.multiple);
			}

			return true;
		},

		/**
		 * Handles input event of each field or subform
		 */
		handleInput(e: Event | any, field: FormBuilderComputedField, validate: (value: any) => void) {
			if ([...this.noComponentRequired, "separator"].indexOf(field.type) === -1) {
				let value = null;
				if (e instanceof Event && e.target) {
					value = (e.target as HTMLInputElement).value;
				} else {
					value = e;
				}
				/**
				 * Validate new value
				 */
				if (this.mode !== "passive") {
					validate(value);
				}
				/**
				 * Emit previous values as `beforeInput` event
				 */
				this.$emit("beforeInput", this.getFormValues());
				/**
				 * Sets new value
				 */
				this.setValue(field.id, value, field);
				/**
				 * checking if value is suggested
				 */
				this.checkIfSuggested(field, value);
				/**
				 * Emits input event for consumers to have latest values
				 */
				this.emitInput();
			}
		},

		/**
		 * Set value of field
		 */
		setValue(id: string, value: any, computedFiled: FormBuilderComputedField) {
			const rawField = computedFiled.rawField;

			if (computedFiled.type === "file") {
				const files: any[] = [];
				/**
				 * Check existing files
				 */
				if (this.values[id]) {
					this.values[id].forEach((f: any) => files.push(f));
				}
				/**
				 * Adds new files
				 */
				value.forEach((f: any) => files.push(f));
				/**
				 * Update latest files array
				 */
				this.values[id] = files;

				if (rawField && rawField.type === "file") {
					rawField.value = files;
				}
			} else {
				/**
				 * Set new value
				 */
				this.values[id] = value;

				if (rawField && "value" in rawField) {
					rawField.value = value;
				}
			}
		},

		emitInput() {
			/**
			 * Whenever value of any field changes
			 *
			 * @event input
			 */
			this.$emit("input", this.getFormValues());
		},

		/**
		 * Add subform values
		 */
		handleInitialValues(value: any, field: FormBuilderComputedField) {
			/**
			 * Sets sub form value
			 */
			this.setValue(field.id, value, field);
		},

		/**
		 * Removes files from file array based on name
		 */
		removeFile(filename: string, field: FormBuilderComputedField, validate: (value: any) => void) {
			const files = this.values[field.id];
			const index = files.findIndex((f: any) => f.name === filename);
			if (index > -1) {
				files.splice(index, 1);
			}
			validate(files);
			/**
			 * emit input event to notify consumer with updated values
			 */
			this.emitInput();
		},

		async validateAndSubmit() {
			const resultFlag = await this.validateForm().then(result => {
				/**
				 * When form submitted <br/>
				 * It returns {valid : Boolean, values : [ { id: String, value: String|Number|Array},...]}
				 *
				 * @event submit
				 */
				this.$emit("submit", { valid: result, values: this.getFormValues() });

				return result;
			});

			return resultFlag;
		},

		/**
		 * Form validation api
		 */
		validateForm(): Promise<boolean> {
			return (this.$refs["flow-form"] as any).validate();
		},

		/**
		 * Handle field click event
		 */
		handleClick(event: Event, field: FormBuilderComputedField) {
			if (
				field.type === "submit" ||
				field.type === "button" ||
				field.type === "button-icon" ||
				field.type === "avatar"
			) {
				event.preventDefault();
				if (field.type === "submit") {
					this.validateAndSubmit();
				}
			}

			if ("onClick" in field && field.onClick) {
				field.onClick();
			}
		},

		handleTextClick(text?: FormBuilderText) {
			if (text?.onClick) {
				text.onClick();
			}
		},

		enableInfoSlot(field: FormBuilderComputedField) {
			const hasFieldInfoIcon =
				"info" in field &&
				typeof field.info === "object" &&
				typeof field.info.icon === "boolean" &&
				!field.info.icon;

			const hasFieldFlags = "flags" in field && field.flags && field.flags.length > 0;

			return hasFieldInfoIcon || hasFieldFlags;
		},

		addOption(field: FormBuilderComputedField, newoption: string) {
			if (field.type !== "select") {
				return;
			}

			// eslint-disable-next-line no-console
			//@ts-expect-error
			const selectRef = this.$refs[field.id][0];
			const options = field.options;
			if (options) {
				options.push(newoption);
			}
			selectRef.select(newoption, newoption);
		},

		checkErrorsState(field: FormBuilderComputedField, errors: unknown[], passed: unknown) {
			if (errors && errors.length > 0) {
				return "error";
			} else if (
				passed &&
				field.component === "InputText" &&
				"successTooltip" in field.props &&
				field.props.successTooltip
			) {
				return "success";
			} else if ("state" in field.props && field.props.state) {
				return field.props.state;
			} else {
				return "default";
			}
		},

		isOldSafari() {
			// check safari less than 15 version
			return BrowserVersion.isOldSafari();
		},

		getFieldLabel(field: FormBuilderComputedField) {
			if ("label" in field && field.label) {
				return typeof field.label === "string" ? field.label : field.label.text;
			}

			return "";
		}
	}
});

function isIDField(field: FormBuilderFields): field is FormBuilderIDField {
	return "type" in field && field.type === "id";
}
</script>
<style lang="scss">
div.flow-form-builder {
	display: flex;
	align-items: stretch;
	grid-gap: 16px;
	gap: 16px;
	padding: 2px;
	width: 100%;
	overflow: visible;
	&[in-accordion] {
		padding: 2px 2px 8px 2px;
	}

	div.flow-form-field-wrapper {
		margin-bottom: 0px;
	}
	div.flow-form-builder-separator {
		justify-content: center;
	}
	div.flow-form-builder-hidden {
		display: none;
	}
	div.flow-form-field-wrapper[inline] {
		align-self: flex-start;
		&.flow-form-builder-header {
			align-self: center;
		}
		&.flow-form-builder-icon {
			align-self: center;
		}
	}

	&[data-direction="column"] {
		flex-direction: column;
	}
	&[data-direction="row"] {
		flex-direction: row;
		> [inline] {
			flex: 0 0 auto !important;
		}
		> *:not(button[state="icon"]) {
			flex: 1 1 auto;
		}
		overflow: visible;
	}

	div.flow-form-field-wrapper > .flow-form-field-label-wrapper {
		min-height: 12px;
	}

	.multiselect__option--selected > div.container {
		padding: 8px 8px 8px 0px !important;
	}
	.multiselect__select {
		height: 100% !important;
		&:before {
			top: 58% !important;
		}
	}

	div.slab-hierarchy.form-selected-file > div.slab-container[type="filled"] {
		background-color: var(--dashboard-input) !important;
	}
	.flow-form-builder-accordion + *:not(.flow-form-builder-accordion) {
		margin-top: 12px;
	}

	div.accordion-container[bottomBorder] {
		margin-bottom: -12px;
	}
}

/**
* gap is not supported in Safari
 */
@media not all and (min-resolution: 0.001dpcm) {
	@supports (-webkit-appearance: none) {
		div.flow-form-builder[data-old-safari] {
			&[data-direction="column"] {
				> * + * {
					margin-top: 12px;
				}
			}
			&[data-direction="row"] {
				> * + * {
					margin-left: 12px;
				}
			}
		}
	}
}
</style>
